// frontend/admin-portal/src/components/orders/OrderItemsEditor.jsx
import React, { useState } from "react";
import { Button, Input, formatMoney } from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";

export default function OrderItemsEditor({
  items = [],
  onChange,
  currency = "USD",
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchProducts = async (q) => {
    setSearch(q);
    if (!q) {
      setProducts([]);
      return;
    }
    setLoading(true);
    try {
      const { items } = await productService.list({
        search: q,
        limit: 20,
        isActive: true,
      });
      setProducts(items || []);
    } finally {
      setLoading(false);
    }
  };

  const addProduct = (p) => {
    onChange([
      ...items,
      {
        productId: p.id,
        productName: p.name,
        productSku: p.sku,
        unit: p.unit || "pcs",
        quantity: 1,
        unitPrice: Number(p.price) || 0,
        discountPercent: 0,
        notes: "",
      },
    ]);
    setShowPicker(false);
    setSearch("");
    setProducts([]);
  };

  const addFree = () => {
    onChange([
      ...items,
      {
        productId: null,
        productName: "",
        productSku: "",
        unit: "pcs",
        quantity: 1,
        unitPrice: 0,
        discountPercent: 0,
        notes: "",
      },
    ]);
  };

  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  const lineTotal = (it) =>
    Number(it.quantity) *
    Number(it.unitPrice) *
    (1 - (Number(it.discountPercent) || 0) / 100);

  const grandTotal = items.reduce((s, it) => s + lineTotal(it), 0);

  return (
    <div>
      <div className="d-flex gap-2 mb-3 flex-wrap">
        <Button
          size="sm"
          variant="primary"
          onClick={() => setShowPicker((v) => !v)}
        >
          + Add from catalog
        </Button>
        <Button size="sm" variant="outline" onClick={addFree}>
          + Add custom item
        </Button>
      </div>

      {showPicker && (
        <div className="fh-prod-picker mb-3">
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => searchProducts(e.target.value)}
          />
          <div className="fh-prod-picker__list">
            {loading && <div className="text-muted p-3">Loading…</div>}
            {!loading &&
              products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="fh-prod-picker__item"
                  onClick={() => addProduct(p)}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{p.name}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {p.sku} {p.category ? `· ${p.category.name}` : ""}
                    </div>
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {formatMoney(p.price, { symbol: currency })} · stock{" "}
                    {p.stock}
                  </div>
                </button>
              ))}
            {!loading && search && products.length === 0 && (
              <div className="text-muted p-3">No products found</div>
            )}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div
          className="text-center text-muted p-4"
          style={{ border: "1px dashed var(--fh-border)", borderRadius: 10 }}
        >
          No items yet. Add products from the catalog or create custom items.
        </div>
      )}

      {items.length > 0 && (
        <div className="fh-order-items">
          <div className="fh-order-items__head">
            <div>Item</div>
            <div>Qty</div>
            <div>Unit</div>
            <div>Unit Price</div>
            <div>Disc %</div>
            <div>Line Total</div>
            <div></div>
          </div>
          {items.map((it, idx) => (
            <div className="fh-order-items__row" key={idx}>
              <div>
                {it.productId ? (
                  <>
                    <div style={{ fontWeight: 600 }}>{it.productName}</div>
                    <div className="text-muted" style={{ fontSize: 11.5 }}>
                      {it.productSku}
                    </div>
                  </>
                ) : (
                  <>
                    <Input
                      placeholder="Item name"
                      value={it.productName}
                      onChange={(e) =>
                        update(idx, { productName: e.target.value })
                      }
                    />
                    <Input
                      placeholder="SKU (optional)"
                      value={it.productSku || ""}
                      onChange={(e) =>
                        update(idx, { productSku: e.target.value })
                      }
                    />
                  </>
                )}
              </div>
              <div>
                <Input
                  type="number"
                  step="0.001"
                  min={0.001}
                  value={it.quantity}
                  onChange={(e) =>
                    update(idx, { quantity: Number(e.target.value) || 0 })
                  }
                />
              </div>
              <div>
                <Input
                  value={it.unit}
                  onChange={(e) => update(idx, { unit: e.target.value })}
                />
              </div>
              <div>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={it.unitPrice}
                  onChange={(e) =>
                    update(idx, { unitPrice: Number(e.target.value) || 0 })
                  }
                />
              </div>
              <div>
                <Input
                  type="number"
                  step="0.1"
                  min={0}
                  max={100}
                  value={it.discountPercent}
                  onChange={(e) =>
                    update(idx, {
                      discountPercent: Number(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div style={{ fontWeight: 600 }}>
                {formatMoney(lineTotal(it), { symbol: currency })}
              </div>
              <div>
                <Button size="sm" variant="ghost" onClick={() => remove(idx)}>
                  ×
                </Button>
              </div>
            </div>
          ))}
          <div className="fh-order-items__footer">
            <span>Items total</span>
            <strong>{formatMoney(grandTotal, { symbol: currency })}</strong>
          </div>
        </div>
      )}
    </div>
  );
}
